import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Phone, Mail, Trash2, Edit, Shield, HardHat as HardHatIcon, Check, X, CalendarOff, AlertTriangle } from "lucide-react";
import { Chip } from "@/components/StatusBadge";
import { ABSENCE_STATUS, ABSENCE_TYPE, fmtDate } from "@/lib/domain";
import { toast, Toaster } from "sonner";

const empty = { email: "", name: "", password: "", role: "worker", phone: "", hourly_rate: 42 };

export default function Workers() {
  const [workers, setWorkers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [workersError, setWorkersError] = useState(null);

  const load = () => {
    setLoading(true);
    setWorkersError(null);
    api.get("/workers")
      .then((r) => setWorkers(r.data))
      .catch((e) => setWorkersError(formatApiErrorDetail(e.response?.data?.detail) || "Сотрудники недоступны"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (w) => { setEditing(w); setForm({ ...empty, ...w, password: "" }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const payload = { ...form };
        delete payload.id; delete payload.email;
        if (!payload.password) delete payload.password;
        await api.patch(`/workers/${editing.id}`, payload);
        toast.success("Обновлено");
      } else {
        if (!form.password) return toast.error("Задайте пароль для нового мастера");
        await api.post("/workers", { ...form, hourly_rate: Number(form.hourly_rate) || 0 });
        toast.success("Мастер добавлен");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error("Ошибка: " + (e.response?.data?.detail || e.message));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Удалить?")) return;
    await api.delete(`/workers/${id}`);
    load();
  };

  const [absences, setAbsences] = useState([]);
  const [absencesError, setAbsencesError] = useState(null);
  const [absenceTab, setAbsenceTab] = useState("pending");

  const loadAbsences = () => {
    api.get("/absences")
      .then((r) => { setAbsences(r.data); setAbsencesError(null); })
      .catch((e) => setAbsencesError(formatApiErrorDetail(e.response?.data?.detail) || "Нерабочие периоды недоступны"));
  };
  useEffect(() => { loadAbsences(); }, []);

  const groupedAbsences = useMemo(() => ({
    pending: absences.filter((a) => a.status === "pending"),
    approved: absences.filter((a) => a.status === "approved"),
    rejected: absences.filter((a) => a.status === "rejected"),
  }), [absences]);

  const decideAbsence = async (a, decision) => {
    try {
      await api.post(`/absences/${a.id}/${decision}`);
      toast.success(decision === "approve" ? "Согласовано" : "Отклонено");
      loadAbsences();
    } catch (e) {
      toast.error("Ошибка: " + formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <Toaster theme="dark" position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-400/80 mb-2 font-medium">Сотрудники</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl">Бригада</h1>
          <p className="text-slate-400 mt-1 text-sm">{workers.length} сотрудников</p>
        </div>
        <Button data-testid="new-worker-button" onClick={startNew} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
          <Plus size={16} /> Добавить
        </Button>
      </div>

      {workersError && (
        <Card className="p-4 bg-red-500/10 border-red-500/30 text-sm text-red-300 flex items-center justify-between gap-3">
          <span className="flex items-center gap-2"><AlertTriangle size={16} /> {workersError}</span>
          <Button size="sm" variant="outline" onClick={load} className="bg-transparent border-red-500/30 hover:bg-red-500/10">Повторить</Button>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && <Card className="p-8 bg-[#131924] border-[#212b3d] text-center text-slate-500">Загрузка сотрудников…</Card>}
        {!loading && !workersError && workers.length === 0 && (
          <Card className="p-8 bg-[#131924] border-[#212b3d] text-center text-slate-500">Сотрудников пока нет.</Card>
        )}
        {!loading && workers.map((w) => (
          <Card key={w.id} data-testid={`worker-card-${w.id}`} className="p-5 bg-[#131924] border-[#212b3d] hover:border-amber-500/30 transition">
            <div className="flex items-start gap-3 mb-3">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center font-semibold text-lg border ${
                w.role === "admin" ? "bg-amber-500/15 border-amber-500/40 text-amber-400" : "bg-blue-500/15 border-blue-500/40 text-blue-300"
              }`}>{(w.name || "?")[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-slate-100 truncate">{w.name}</div>
                <Chip className={w.role === "admin"
                  ? "bg-amber-500/15 text-amber-300 border-amber-500/30 mt-1"
                  : "bg-blue-500/15 text-blue-300 border-blue-500/30 mt-1"}>
                  {w.role === "admin" ? <><Shield size={10} className="inline mr-1"/>Владелец</> : <><HardHatIcon size={10} className="inline mr-1"/>Мастер</>}
                </Chip>
              </div>
            </div>
            <div className="space-y-1 text-xs text-slate-400 mb-4">
              <div className="flex items-center gap-2"><Mail size={12} /> <span className="truncate">{w.email}</span></div>
              {w.phone && <div className="flex items-center gap-2"><Phone size={12} /> {w.phone}</div>}
              {w.hourly_rate && <div className="text-slate-500">Ставка: <span className="text-slate-300">{w.hourly_rate} €/ч</span></div>}
            </div>
            <div className="flex gap-2 pt-3 border-t border-[#212b3d]">
              <Button size="sm" variant="outline" onClick={() => startEdit(w)} className="flex-1 bg-transparent border-[#212b3d] hover:bg-[#1c2434]"><Edit size={13} /> Изменить</Button>
              {w.role !== "admin" && (
                <Button size="sm" variant="outline" onClick={() => del(w.id)}
                  className="bg-transparent border-[#212b3d] hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/40">
                  <Trash2 size={13} />
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      <div>
        <h2 className="font-display font-semibold text-xl mb-3 flex items-center gap-2">
          <CalendarOff size={18} className="text-amber-400" /> Нерабочие периоды
        </h2>
        {absencesError && (
          <Card className="p-4 bg-red-500/10 border-red-500/30 text-sm text-red-300 mb-3">{absencesError}</Card>
        )}
        {!absencesError && (
          <Tabs value={absenceTab} onValueChange={setAbsenceTab}>
            <TabsList className="bg-[#131924] border border-[#212b3d]">
              <TabsTrigger data-testid="absence-tab-pending" value="pending">На согласовании ({groupedAbsences.pending.length})</TabsTrigger>
              <TabsTrigger data-testid="absence-tab-approved" value="approved">Согласовано ({groupedAbsences.approved.length})</TabsTrigger>
              <TabsTrigger data-testid="absence-tab-rejected" value="rejected">Отклонено ({groupedAbsences.rejected.length})</TabsTrigger>
            </TabsList>
            {["pending", "approved", "rejected"].map((key) => (
              <TabsContent key={key} value={key} className="mt-3 space-y-2">
                {groupedAbsences[key].length === 0 && (
                  <Card className="p-6 bg-[#131924] border-[#212b3d] text-center text-slate-500 text-sm">Пусто.</Card>
                )}
                {groupedAbsences[key].map((a) => {
                  const st = ABSENCE_STATUS[a.status] || { label: a.status, cls: "" };
                  return (
                    <Card key={a.id} data-testid={`absence-card-${a.id}`}
                      className="p-4 bg-[#131924] border-[#212b3d] flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <div className="font-medium text-slate-100 truncate">
                            {a.crm_worker?.name || `Сотрудник ${String(a.worker_id || "").slice(-6)}`}
                          </div>
                          <Chip className={st.cls}>{st.label}</Chip>
                          {a.type && <Chip className="bg-slate-500/15 text-slate-300 border-slate-500/30">{ABSENCE_TYPE[a.type] || a.type}</Chip>}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {fmtDate(a.from_at || a.starts_at)} → {fmtDate(a.to_at || a.ends_at)}
                          {a.note && <span> · {a.note}</span>}
                        </div>
                      </div>
                      {a.status === "pending" && (
                        <div className="flex gap-2 shrink-0">
                          <Button size="sm" data-testid={`absence-approve-${a.id}`} onClick={() => decideAbsence(a, "approve")}
                            className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30">
                            <Check size={14} />
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`absence-reject-${a.id}`} onClick={() => decideAbsence(a, "reject")}
                            className="bg-transparent border-[#212b3d] hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/40">
                            <X size={14} />
                          </Button>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131924] border-[#212b3d] text-slate-100 max-w-md">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Редактировать" : "Новый сотрудник"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Имя</Label><Input data-testid="worker-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
            <div><Label>Эл. почта</Label><Input data-testid="worker-email-input" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0f1521] border-[#212b3d] disabled:opacity-60" /></div>
            <div><Label>{editing ? "Новый пароль (опц.)" : "Пароль *"}</Label><Input data-testid="worker-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
            <div><Label>Телефон</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
            <div><Label>Ставка, €/час</Label><Input type="number" value={form.hourly_rate || 0} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
            <div>
              <Label>Роль</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="worker-role-select" className="bg-[#0f1521] border-[#212b3d]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#131924] border-[#212b3d] text-slate-100">
                  <SelectItem value="worker">Мастер / Рабочий</SelectItem>
                  <SelectItem value="admin">Владелец / Админ</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-worker-button" onClick={save} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
